import React, { useState, useEffect } from 'react';
import { Shield, RefreshCw, AlertTriangle, CheckCircle, Calendar, Download, Trash2, Plus, Upload } from 'lucide-react';
import { toast } from 'react-toastify';

const SSLCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renewingCert, setRenewingCert] = useState(null);
  const [deletingCert, setDeletingCert] = useState(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'generate' or 'upload'
  const [newCert, setNewCert] = useState({
    domain: '',
    validityDays: 365,
    certificate: '',
    privateKey: '',
    chain: ''
  });
  const [processing, setProcessing] = useState(false);

  // API functions
  const fetchCertificates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/proxy/ssl/certificates', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setCertificates(result.data);
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch SSL certificates');
      console.error('Error fetching certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const renewCertificate = async (domain) => {
    setRenewingCert(domain);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/proxy/ssl/certificates/${encodeURIComponent(domain)}/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Certificate for ${domain} renewed successfully`);
        await fetchCertificates();
      } else {
        toast.error(result.message || 'Failed to renew certificate');
      }
    } catch (err) {
      toast.error('Failed to renew certificate');
      console.error('Error renewing certificate:', err);
    } finally {
      setRenewingCert(null);
    }
  };

  const deleteCertificate = async (domain) => {
    if (!window.confirm(`Are you sure you want to delete the certificate for "${domain}"?`)) {
      return;
    }

    setDeletingCert(domain);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/proxy/ssl/certificates/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Certificate for ${domain} deleted successfully`);
        await fetchCertificates();
      } else {
        toast.error(result.message || 'Failed to delete certificate');
      }
    } catch (err) {
      toast.error('Failed to delete certificate');
      console.error('Error deleting certificate:', err);
    } finally {
      setDeletingCert(null);
    }
  };

  const handleAddCertificate = async () => {
    if (!newCert.domain) {
      toast.error('Domain is required');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint, body;

      if (modalType === 'generate') {
        endpoint = '/api/proxy/ssl/certificates/generate';
        body = {
          domain: newCert.domain,
          validityDays: newCert.validityDays
        };
      } else {
        if (!newCert.certificate || !newCert.privateKey) {
          toast.error('Certificate and private key are required');
          return;
        }
        endpoint = '/api/proxy/ssl/certificates/install';
        body = {
          domain: newCert.domain,
          certificate: newCert.certificate,
          privateKey: newCert.privateKey,
          chain: newCert.chain
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Certificate ${modalType === 'generate' ? 'generated' : 'installed'} successfully`);
        setNewCert({ domain: '', validityDays: 365, certificate: '', privateKey: '', chain: '' });
        setShowAddModal(false);
        await fetchCertificates();
      } else {
        toast.error(result.message || `Failed to ${modalType} certificate`);
      }
    } catch (err) {
      toast.error(`Failed to ${modalType} certificate`);
      console.error(`Error ${modalType}ing certificate:`, err);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'text-green-400';
      case 'expired': return 'text-red-400';
      case 'expiring': return 'text-yellow-400';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle className="w-4 h-4" />;
      case 'expired': case 'expiring': return <AlertTriangle className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  useEffect(() => {
    fetchCertificates();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchCertificates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading SSL certificates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Error loading SSL certificates</span>
        </div>
        <p className="text-red-300 mt-2">{error}</p>
        <button 
          onClick={() => { setLoading(true); fetchCertificates(); }}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SSL Certificates */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">SSL Certificate Management</h2>
                <p className="text-slate-400 mt-1">Manage SSL certificates and security configurations</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => { setLoading(true); fetchCertificates(); }}
                disabled={loading}
                className="px-3 py-2 bg-slate-700 text-slate-200 rounded-md hover:bg-slate-600 flex items-center disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <div className="relative">
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Certificate
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {certificates.map(cert => (
              <div key={cert.id} className="bg-slate-900 border border-slate-600 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="text-lg font-medium text-slate-200">{cert.domain}</h3>
                      <div className={`flex items-center mt-1 ${getStatusColor(cert.status)}`}>
                        {getStatusIcon(cert.status)}
                        <span className="ml-2 text-sm capitalize">{cert.status}</span>
                        {cert.daysUntilExpiry <= 30 && cert.daysUntilExpiry > 0 && (
                          <span className="ml-2 text-yellow-400">({cert.daysUntilExpiry} days left)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {cert.type === 'Let\'s Encrypt' && (
                      <button 
                        onClick={() => renewCertificate(cert.domain)}
                        disabled={renewingCert === cert.domain}
                        className="p-2 text-slate-400 hover:text-green-400 disabled:opacity-50"
                        title="Renew certificate"
                      >
                        <RefreshCw className={`w-4 h-4 ${renewingCert === cert.domain ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                    <button 
                      onClick={() => deleteCertificate(cert.domain)}
                      disabled={deletingCert === cert.domain}
                      className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-50"
                      title="Delete certificate"
                    >
                      <Trash2 className={`w-4 h-4 ${deletingCert === cert.domain ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Type:</span>
                    <div className="text-slate-200">{cert.type}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Issuer:</span>
                    <div className="text-slate-200">{cert.issuer}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Valid From:</span>
                    <div className="text-slate-200">{formatDate(cert.validFrom)}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Valid To:</span>
                    <div className="text-slate-200">{formatDate(cert.validTo)}</div>
                  </div>
                </div>

                {cert.autoRenewal && (
                  <div className="mt-3 pt-3 border-t border-slate-600">
                    <div className="flex items-center text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Auto-renewal enabled
                    </div>
                  </div>
                )}
              </div>
            ))}

            {certificates.length === 0 && (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">No SSL Certificates</h3>
                <p className="text-slate-400 mb-4">No SSL certificates found. Add one to get started.</p>
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Certificate
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Certificate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-200 mb-4">Add SSL Certificate</h3>
            
            {!modalType && (
              <div className="space-y-4">
                <p className="text-slate-400">Choose how you want to add a certificate:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setModalType('generate')}
                    className="p-4 bg-slate-900 border border-slate-600 rounded-lg hover:bg-slate-700 text-left"
                  >
                    <Shield className="w-8 h-8 text-blue-400 mb-2" />
                    <h4 className="font-medium text-slate-200 mb-1">Generate Self-Signed</h4>
                    <p className="text-sm text-slate-400">Generate a new self-signed certificate</p>
                  </button>
                  <button
                    onClick={() => setModalType('upload')}
                    className="p-4 bg-slate-900 border border-slate-600 rounded-lg hover:bg-slate-700 text-left"
                  >
                    <Upload className="w-8 h-8 text-green-400 mb-2" />
                    <h4 className="font-medium text-slate-200 mb-1">Upload Certificate</h4>
                    <p className="text-sm text-slate-400">Install an existing certificate</p>
                  </button>
                </div>
              </div>
            )}

            {modalType === 'generate' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Domain Name
                  </label>
                  <input
                    type="text"
                    value={newCert.domain}
                    onChange={(e) => setNewCert({...newCert, domain: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Validity (Days)
                  </label>
                  <input
                    type="number"
                    value={newCert.validityDays}
                    onChange={(e) => setNewCert({...newCert, validityDays: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="3650"
                  />
                </div>
              </div>
            )}

            {modalType === 'upload' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Domain Name
                  </label>
                  <input
                    type="text"
                    value={newCert.domain}
                    onChange={(e) => setNewCert({...newCert, domain: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Certificate (PEM format)
                  </label>
                  <textarea
                    value={newCert.certificate}
                    onChange={(e) => setNewCert({...newCert, certificate: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    rows="6"
                    placeholder="-----BEGIN CERTIFICATE-----"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Private Key (PEM format)
                  </label>
                  <textarea
                    value={newCert.privateKey}
                    onChange={(e) => setNewCert({...newCert, privateKey: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    rows="6"
                    placeholder="-----BEGIN PRIVATE KEY-----"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Certificate Chain (Optional)
                  </label>
                  <textarea
                    value={newCert.chain}
                    onChange={(e) => setNewCert({...newCert, chain: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    rows="4"
                    placeholder="-----BEGIN CERTIFICATE-----"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setModalType('');
                  setNewCert({ domain: '', validityDays: 365, certificate: '', privateKey: '', chain: '' });
                }}
                className="px-4 py-2 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              {modalType && (
                <button
                  onClick={handleAddCertificate}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {processing ? `${modalType === 'generate' ? 'Generating' : 'Installing'}...` : `${modalType === 'generate' ? 'Generate' : 'Install'} Certificate`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSLCertificates;